import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { API } from '@/config/api';

/**
 * The page the password reset email links to (/reset-password?token=...).
 * The link works once, for an hour (routes/password_reset).
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState('checking'); // checking | ready | invalid | done
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    axios.post(`${API}/auth/validate-reset-token`, { token })
      .then(res => setState(res.data.valid ? 'ready' : 'invalid'))
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setState('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset the password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reset your password</CardTitle>
          <CardDescription>Chain29</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'checking' && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
          {state === 'invalid' && (
            <div className="space-y-4 text-center" data-testid="reset-invalid">
              <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
              <p className="text-sm">This reset link is invalid or has expired. Ask for a new one from the sign-in page.</p>
              <Button asChild className="w-full"><Link to="/customer/login">Back to sign in</Link></Button>
            </div>
          )}
          {state === 'ready' && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <PasswordInput id="new-password" value={password} onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password" toggleClassName="hover:text-slate-700" data-testid="reset-new-password" />
                <p className="text-xs text-muted-foreground">At least 8 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <PasswordInput id="confirm-password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password" toggleClassName="hover:text-slate-700" />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Set new password
              </Button>
            </form>
          )}
          {state === 'done' && (
            <div className="space-y-4 text-center" data-testid="reset-done">
              <CheckCircle className="h-8 w-8 mx-auto text-green-600" />
              <p className="text-sm">Your password was reset. You can now sign in with it.</p>
              <Button asChild className="w-full"><Link to="/customer/login">Sign in</Link></Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
